/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Handles access states: anonymous -> login, pending -> /access-pending,
 * approved/admin -> render children.
 */

import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useRequireAuth } from './AuthContext'
import type { AccessState } from './ssoClient'

interface ProtectedRouteProps {
  children: ReactNode
  /** Optional custom loading component */
  loading?: ReactNode
  /** Minimum access state required (default: 'approved') */
  requiredAccess?: AccessState
}

/**
 * Loading skeleton for protected routes
 */
function DefaultLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[rgb(2,6,15)]">
      <div className="text-center">
        <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin mb-4" />
        <p className="text-white/60 text-sm font-light">
          Checking authentication...
        </p>
      </div>
    </div>
  )
}

const ACCESS_RANK: Record<AccessState, number> = {
  anonymous: 0,
  pending: 1,
  approved: 2,
  admin: 3,
}

/**
 * ProtectedRoute - requires authentication and access level to render children
 *
 * Usage:
 * ```tsx
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <DashboardPage />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export function ProtectedRoute({ children, loading, requiredAccess = 'approved' }: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, accessState } = useRequireAuth()

  // Show loading while checking auth
  if (isLoading) {
    return <>{loading || <DefaultLoading />}</>
  }

  // If not authenticated, useRequireAuth will redirect to login
  if (!isAuthenticated) {
    return <>{loading || <DefaultLoading />}</>
  }

  // Authenticated but pending approval — redirect to pending page
  if (ACCESS_RANK[accessState] < ACCESS_RANK[requiredAccess]) {
    if (accessState === 'pending') {
      return <Navigate to="/access-pending" replace />
    }
    // Fallback for any other insufficient access
    return <Navigate to="/" replace />
  }

  // Sufficient access — render children
  return <>{children}</>
}

export default ProtectedRoute
